import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

const TabsContext = createContext(null);

/**
 * Accessible tabs primitive.
 *   <Tabs value={active} onValueChange={setActive}>
 *     <TabsList>
 *       <TabsTrigger value="one">One</TabsTrigger>
 *       <TabsTrigger value="two">Two</TabsTrigger>
 *     </TabsList>
 *     <TabsPanel value="one">…</TabsPanel>
 *   </Tabs>
 *
 * Full keyboard: Left/Right/Home/End move focus, Enter/Space activate.
 * Supports `onDark` prop for the dark-surface treatment.
 */
export function Tabs({
  value,
  onValueChange,
  onDark = false,
  className,
  children,
}) {
  const baseId = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const triggersRef = useRef(new Map());
  const registerTrigger = useCallback((val, el) => {
    if (el) triggersRef.current.set(val, el);
    else triggersRef.current.delete(val);
  }, []);
  const ctx = useMemo(
    () => ({
      value,
      onValueChange,
      onDark,
      baseId,
      registerTrigger,
      focusRelative: (fromVal, delta) => {
        const keys = Array.from(triggersRef.current.keys());
        const idx = keys.indexOf(fromVal);
        if (idx === -1) return;
        const next = ((idx + delta) % keys.length + keys.length) % keys.length;
        const el = triggersRef.current.get(keys[next]);
        el?.focus();
        onValueChange(keys[next]);
      },
      focusEdge: (edge) => {
        const keys = Array.from(triggersRef.current.keys());
        const key = edge === "start" ? keys[0] : keys[keys.length - 1];
        triggersRef.current.get(key)?.focus();
        onValueChange(key);
      },
    }),
    [value, onValueChange, onDark, baseId, registerTrigger],
  );
  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, children, "aria-label": ariaLabel }) {
  const { onDark } = useContext(TabsContext);
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-1 rounded-[10px] p-1",
        onDark ? "bg-[rgba(237,241,245,0.06)]" : "bg-[#DDE2E9]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({ value, children, className, disabled, ...rest }) {
  const { value: active, onValueChange, onDark, baseId, registerTrigger, focusRelative, focusEdge } =
    useContext(TabsContext);
  const isActive = active === value;
  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={`${baseId}-panel-${value}`}
      id={`${baseId}-tab-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      ref={(el) => registerTrigger(value, el)}
      onClick={() => onValueChange(value)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          focusRelative(value, 1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          focusRelative(value, -1);
        } else if (e.key === "Home") {
          e.preventDefault();
          focusEdge("start");
        } else if (e.key === "End") {
          e.preventDefault();
          focusEdge("end");
        }
      }}
      className={cn(
        "rounded-[6px] px-4 py-[7px] text-[14px] font-semibold transition-colors duration-fast ease-out disabled:opacity-45",
        isActive
          ? onDark
            ? "bg-surface-elevated text-ink-primary shadow-elev-sm"
            : "bg-white text-frac-purple shadow-elev-sm"
          : onDark
            ? "text-ink-secondary hover:text-ink-primary"
            : "text-frac-gray-70 hover:text-frac-purple",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TabsPanel({ value, children, className, ...rest }) {
  const { value: active, baseId } = useContext(TabsContext);
  if (active !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      tabIndex={0}
      className={cn("focus:outline-none", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export default Tabs;
